import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  useColorMode,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  FolderSync,
  ClipboardCheck,
  FileText,
  KeyRound,
  Users,
  RefreshCw,
} from 'lucide-react'

const MotionBox = motion.create(Box)

const fadeInUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

interface UseCaseCardProps {
  icon: React.ElementType
  title: string
  description: string
  isDark: boolean
}

function UseCaseCard({ icon, title, description, isDark }: UseCaseCardProps) {
  const bg = isDark ? 'stone.800' : 'white'
  const border = isDark ? 'stone.700' : 'stone.200'
  const titleColor = isDark ? 'stone.100' : 'stone.800'
  const descColor = isDark ? 'stone.400' : 'stone.500'

  return (
    <MotionBox
      variants={fadeInUp}
      bg={bg}
      p={6}
      borderRadius="12px"
      border="1px solid"
      borderColor={border}
      style={{ transition: 'all 0.2s' }}
      _hover={{
        borderColor: 'brand.500',
        transform: 'translateY(-2px)',
      }}
    >
      <Icon as={icon} boxSize={5} color="brand.500" mb={3} />
      <Text fontWeight={600} color={titleColor} mb={2}>
        {title}
      </Text>
      <Text fontSize="sm" color={descColor} lineHeight={1.6}>
        {description}
      </Text>
    </MotionBox>
  )
}

export function UseCasesSection() {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  const useCases = [
    {
      icon: FolderSync,
      title: 'AI Context Sync',
      description: 'Store Cursor rules, project plans, and skill definitions. Load them into any session on any machine.',
    },
    {
      icon: ClipboardCheck,
      title: 'QA Reports',
      description: 'Share test results, bug reports, and benchmarks with encrypted links. No more Slack pastes.',
    },
    {
      icon: FileText,
      title: 'Architecture Docs',
      description: 'Implementation plans, RFCs, and design docs. Set long expiration for persistent reference.',
    },
    {
      icon: KeyRound,
      title: 'Secrets & Env Vars',
      description: 'Share API keys and credentials securely. Burn-after-read ensures one-time access.',
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Runbooks, onboarding guides, internal docs. Anyone with the link can view—no account needed.',
    },
    {
      icon: RefreshCw,
      title: 'Scratch Notes',
      description: 'Quick markdown that needs a link for an hour or a day. Meeting notes, debug logs, snippets.',
    },
  ]

  return (
    <Box
      id="use-cases"
      scrollMarginTop="72px"
      py={{ base: 16, md: 24 }}
      bg={isDark ? 'transparent' : 'white'}
    >
      <Container maxW="960px">
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={staggerContainer}
        >
          <Box textAlign="center" mb={12}>
            <MotionBox variants={fadeInUp}>
              <Heading
                as="h2"
                fontFamily="heading"
                fontWeight={700}
                fontSize={{ base: '1.75rem', md: '2.25rem' }}
                letterSpacing="-0.025em"
                color={isDark ? 'stone.50' : 'stone.900'}
                mb={4}
              >
                Built for real workflows.
              </Heading>
              <Text
                fontSize="1.125rem"
                color="stone.400"
                maxW="560px"
                mx="auto"
              >
                From AI-generated docs to sensitive credentials, myrkup handles it all with encryption and expiration controls.
              </Text>
            </MotionBox>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {useCases.map((useCase) => (
              <UseCaseCard
                key={useCase.title}
                icon={useCase.icon}
                title={useCase.title}
                description={useCase.description}
                isDark={isDark}
              />
            ))}
          </SimpleGrid>
        </MotionBox>
      </Container>
    </Box>
  )
}
